import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import VacancyResponsesInline from '../VacancyResponsesInline';
import ResponseActionModal from './ResponseActionModal';
import ResponseAutoParseStartModal from './ResponseAutoParseStartModal';
import { ChevronDownIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  ALL_AVITO_APPLICATION_STATES,
  DEFAULT_AVITO_APPLICATION_STATE,
  ResponsesFiltersState,
  RESPONSES_EXPERIENCE_OPTIONS,
  RESPONSES_ORDER_OPTIONS,
  EMPLOYER_STATES_OPTIONS,
} from '../../hooks/useResponsesFilters';
import { SearchProgress } from '../hooks/useSearchProgress';
import ExperienceModal from '../../components/ui/ExperienceModal';
import AreaModal from '../../components/ui/AreaModal';
import { FilterPopup } from '../../components/search/FilterPopup';
import FilterButton from '../../components/search/FilterButton';
import { defaultFiltersState } from '../../hooks/useFilters';
import { useBrandingColors } from '../../hooks/useBrandingColors';
import IconManualParse from '../assets/icons/icon-manual-parse.svg';
import IconAutoParse from '../assets/icons/icon-auto-parse.svg';
import {
  applyAvitoApplicationAction,
  checkCampaignCandidates,
  getAvitoApplicationStates,
  openHHResumeContacts,
} from '../services/api';
import ResponsesFiltersModal, { ResponsesFiltersInitialSection } from '../../components/search/ResponsesFiltersModal';
import CampaignCheckResultsModal from '../CampaignCheckResultsModal';
import { partitionClarifyCandidatesByCheck } from '../utils/partitionClarifyCandidatesByCheck';
import { checkCampaignCreationCredits, createCampaign } from '../../services/aiRecruiterService';
import { useEstaffLinkGuard } from '../../hooks/useEstaffLinkGuard';
import { useAuthStore } from '../../store/authStore';
import { useVacancyStore } from '../../store/vacancyStore';
import { buildRelevanceFields, evaluationToRelevanceInput } from '../../sources/relevance';
import { generatePageNumbers } from '../../components/vacancies/helpers';
import { loadLatestCampaignReusableSettings } from '../utils/campaignSettings';
import {
  buildClarifyExperienceCampaignData,
  buildClarifyExperienceCandidate,
  hasClarifyCandidateContact,
  isOpenedContactsReadyForClarify,
  MAILINGS_NAV_STATE_KEY,
} from '../../constants/clarifyExperience';
import {
  buildAvitoResponseCampaignCandidate,
  buildAvitoResponsesCampaignData,
  findAvitoMaybeSuitableStatus,
  getAvitoApplicationId,
  getAvitoChatId,
  getAvitoPhone,
  getAvitoResumeId,
} from '../utils/avitoResponsesCampaign';
import {
  AUTO_PARSE_ACTION_LABELS,
  AUTO_PARSE_BUCKETS,
  AutoParseAction,
  createDefaultAutoParseConfig,
  getAutoParseBucketCounts,
  groupResponsesForAutoParse,
  loadAutoParseActionTemplates,
  sendAutoParseHHAction,
} from '../utils/responseAutoParse';

interface ResponsesTabProps {
  selectedVacancy: any;
  hhVacancyNames: Record<string, string>;
  allResponsesData?: any[];
  responsesData: any[];
  responsesLoading: boolean;
  responsesRelevanceLoading?: boolean;
  responsesRelevanceApplied?: boolean;
  relevancePreset?: 'high' | 'medium' | 'low' | null;
  relevanceCount?: number;
  onRelevancePresetChange?: (preset: 'high' | 'medium' | 'low' | null) => void;
  onRelevanceCountChange?: (count: number) => void;
  aiEvaluations: Record<string, any>;
  evaluatingIds: Set<string>;
  queuedEvaluations?: Record<string, boolean>;
  onEvaluateResume?: (vacancyId: string, responseId: string, resumeData: any) => Promise<void>;
  onRefresh: () => void;
  onRemoveResponses?: (responseIds: string[]) => void;
  onRelevanceClear?: () => void;
  selectedResponses: Set<string>;
  onSelectedResponsesChange: (selected: Set<string>) => void;
  pagination?: {
    currentPage: number;
    perPage: number;
    totalItems: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    onPerPageChange: (perPage: number) => void;
  };
  responsesLoadLimit?: number;
  onResponsesLoadLimitChange?: (limit: number) => void;
  parseMode?: 'manual' | 'auto';
  onParseModeChange?: (mode: 'manual' | 'auto') => void;
  searchValue?: string;
  onSearchValueChange?: (value: string) => void;
  filters?: ResponsesFiltersState;
  onFiltersChange?: (filters: Partial<ResponsesFiltersState>) => void;
  onSearch?: () => void;
  onClearFilters?: () => void;
  scoringCreditLimitHit?: boolean;
  searchProgress?: SearchProgress | null;
  onCancelSearch?: () => void;
  scoringStats?: { total: number; fromCache: number; newlyScored: number };
  vacancyScoringEnabled?: boolean;
  responsesSource?: string;
}

const ResponsesTab: React.FC<ResponsesTabProps> = ({
  selectedVacancy,
  hhVacancyNames,
  allResponsesData,
  responsesData,
  responsesLoading,
  responsesRelevanceLoading,
  responsesRelevanceApplied,
  relevancePreset,
  relevanceCount,
  onRelevancePresetChange,
  onRelevanceCountChange,
  aiEvaluations,
  evaluatingIds,
  queuedEvaluations,
  onEvaluateResume,
  onRefresh,
  onRemoveResponses,
  onRelevanceClear,
  selectedResponses,
  onSelectedResponsesChange,
  pagination,
  responsesLoadLimit = 100,
  onResponsesLoadLimitChange,
  parseMode: externalParseMode,
  onParseModeChange,
  searchValue: externalSearchValue,
  onSearchValueChange,
  filters,
  onFiltersChange,
  onSearch,
  onClearFilters,
  scoringCreditLimitHit,
  searchProgress,
  onCancelSearch,
  scoringStats,
  vacancyScoringEnabled = true,
  responsesSource = 'hh',
}) => {
  const colors = useBrandingColors();
  const navigate = useNavigate();
  const [internalParseMode, setInternalParseMode] = useState<'manual' | 'auto'>('manual');
  const [actionModalResponses, setActionModalResponses] = useState<any[]>([]);
  const [actionModalInitial, setActionModalInitial] = useState<{ actionId: string; templateId: string } | null>(null);
  const [internalSearchValue, setInternalSearchValue] = useState('');
  const ActionModal = ResponseActionModal as any;
  const isHhResponsesSource = (responsesSource || 'hh').toString().toLowerCase() === 'hh';
  const isAvitoResponsesSource = (responsesSource || '').toString().toLowerCase() === 'avito';
  const selectedResponsesByIdRef = useRef<Map<string, any>>(new Map());
  // TASKNEIROKLYUCH-435 FR-1: guard-хук для проверки привязки к E-Staff вакансии
  const { checkEstaffLinkStatus, guardModal: estaffLinkGuardModal } = useEstaffLinkGuard();

  const [isExperienceModalOpen, setIsExperienceModalOpen] = useState(false);
  const [isAreaModalOpen, setIsAreaModalOpen] = useState(false);
  const [isAgePopupOpen, setIsAgePopupOpen] = useState(false);
  const [agePopupPosition, setAgePopupPosition] = useState({ top: 0, left: 0 });
  const [orderDropdownOpen, setOrderDropdownOpen] = useState(false);
  const [collectionDropdownOpen, setCollectionDropdownOpen] = useState(false);
  const [avitoStatusDropdownOpen, setAvitoStatusDropdownOpen] = useState(false);
  const [showFilters] = useState(true);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [filtersInitialSection, setFiltersInitialSection] = useState<ResponsesFiltersInitialSection | null>(null);
  const [avitoApplicationStates, setAvitoApplicationStates] = useState<any[]>([]);
  const [avitoStatesLoading, setAvitoStatesLoading] = useState(false);
  const [selectedAvitoAction, setSelectedAvitoAction] = useState('');
  const [avitoBulkActionInProgress, setAvitoBulkActionInProgress] = useState(false);
  const [avitoMailingInProgress, setAvitoMailingInProgress] = useState(false);

  const ageButtonRef = useRef<HTMLButtonElement>(null);
  const orderButtonRef = useRef<HTMLButtonElement>(null);
  const collectionButtonRef = useRef<HTMLDivElement>(null);
  const avitoStatusButtonRef = useRef<HTMLDivElement>(null);

  const parseMode = externalParseMode ?? internalParseMode;
  const setParseMode = onParseModeChange ?? setInternalParseMode;
  const searchValue = externalSearchValue ?? internalSearchValue;
  const setSearchValue = onSearchValueChange ?? setInternalSearchValue;
  const [autoParseModalOpen, setAutoParseModalOpen] = useState(false);
  const [autoParseConfig, setAutoParseConfig] = useState<Record<string, AutoParseAction>>(createDefaultAutoParseConfig);
  const [autoParseTemplatesLoading, setAutoParseTemplatesLoading] = useState(false);
  const [autoParseInviteMessage, setAutoParseInviteMessage] = useState('');
  const [autoParseRejectMessage, setAutoParseRejectMessage] = useState('');
  const [autoParseInviteTemplateId, setAutoParseInviteTemplateId] = useState<string | null>(null);
  const [autoParseRejectTemplateId, setAutoParseRejectTemplateId] = useState<string | null>(null);
  const [autoParseRunning, setAutoParseRunning] = useState(false);
  const autoParseStopRequestedRef = useRef(false);

  useEffect(() => {
    if (!isAvitoResponsesSource) return;
    let cancelled = false;
    setAvitoStatesLoading(true);
    getAvitoApplicationStates()
      .then((items) => {
        if (!cancelled) {
          setAvitoApplicationStates(Array.isArray(items) ? items : []);
        }
      })
      .catch(() => {
        if (!cancelled) setAvitoApplicationStates([]);
      })
      .finally(() => {
        if (!cancelled) setAvitoStatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAvitoResponsesSource]);

  useEffect(() => {
    const map = selectedResponsesByIdRef.current;
    const poolRaw = allResponsesData ?? responsesData;
    const pool = Array.isArray(poolRaw) ? poolRaw : [];

    for (const r of pool) {
      const rid = r?.id != null ? String(r.id) : '';
      if (!rid) continue;
      if (selectedResponses.has(rid)) map.set(rid, r);
    }

    for (const rid of Array.from(map.keys())) {
      if (!selectedResponses.has(rid)) map.delete(rid);
    }
  }, [allResponsesData, responsesData, selectedResponses]);

  const getSelectedResponsesList = () => {
    const map = selectedResponsesByIdRef.current;
    const poolRaw = allResponsesData ?? responsesData;
    const pool = Array.isArray(poolRaw) ? poolRaw : [];
    const poolById = new Map(pool.map((r: any) => [String(r?.id ?? ''), r]));

    const out: any[] = [];
    for (const id of Array.from(selectedResponses)) {
      const sid = String(id);
      const item = map.get(sid) ?? poolById.get(sid) ?? null;
      if (item) out.push(item);
    }
    return out;
  };

  const handleOpenAgePopup = () => {
    if (ageButtonRef.current) {
      const rect = ageButtonRef.current.getBoundingClientRect();
      setAgePopupPosition({
        top: rect.bottom + 8,
        left: rect.left
      });
      setIsAgePopupOpen(true);
    }
  };

  const handleSearch = () => {
    if (onSearch) {
      onSearch();
    }
  };

  const handleClearFilters = () => {
    if (onClearFilters) {
      onClearFilters();
    }
  };

  const getAvitoExperienceLabel = () => {
    const from = filters?.avitoExperienceMin;
    const to = filters?.avitoExperienceMax;
    if (from && to) return `Опыт ${from}-${to} лет`;
    if (from) return `Опыт от ${from} лет`;
    if (to) return `Опыт до ${to} лет`;
    return 'Опыт';
  };

  const getAvitoEducationLabel = () => {
    const count = filters?.avitoEducationLevel?.length || 0;
    return count > 0 ? `Образование (${count})` : 'Образование';
  };

  const getAvitoDriverLabel = () => {
    const parts: string[] = [];
    if (filters?.avitoDriverLicence === 'yes') parts.push('есть права');
    if (filters?.avitoDriverLicence === 'no') parts.push('нет прав');
    if ((filters?.avitoDriverLicenceCategory?.length || 0) > 0) {
      parts.push(`категории: ${filters?.avitoDriverLicenceCategory.map((v) => v.toUpperCase()).join(', ')}`);
    }
    return parts.length > 0 ? `Права · ${parts.join(', ')}` : 'Права';
  };

  const openBulkActionModal = (initial: { actionId: string; templateId: string } | null) => {
    if (selectedResponses.size === 0) {
      toast.error('Выберите кандидатов');
      return;
    }
    const selected = getSelectedResponsesList();
    if (selected.length > 0 && selected[0].actions && selected[0].actions.length > 0) {
      setActionModalInitial(initial);
      setActionModalResponses(selected);
    } else {
      toast.error('Нет доступных действий для выбранных откликов');
    }
  };

  const handleActionModalSuccess = () => {
    setActionModalResponses([]);
    setActionModalInitial(null);
    onSelectedResponsesChange(new Set());
    onRefresh();
  };

  const handleCloseActionModal = () => {
    setActionModalResponses([]);
    setActionModalInitial(null);
  };

  const [bulkClarifyingInProgress, setBulkClarifyingInProgress] = useState(false);
  // TASKNEIROKLYUCH-434: модалка проверки доступности кандидатов (e-staff блокировки + «писали месяц»).
  // pendingCampaign хранит уже отфильтрованный список — после подтверждения в модалке
  // им вызывается finalizeClarifyCampaign без повторного запроса в e-staff.
  const [checkResult, setCheckResult] = useState<any>(null);
  const [checkResultMessage, setCheckResultMessage] = useState<string | null>(null);
  const [checkResultsModalOpen, setCheckResultsModalOpen] = useState(false);
  const [pendingCampaign, setPendingCampaign] = useState<{
    candidates: any[];
    candidateResponseIds: string[];
    skippedNoResume: number;
    skippedNoContacts: number;
    skippedContactOpen: number;
    skippedEstaffSync: number;
    estaffVacancyId: string | number | null;
    vacancyTitle: string;
    vacancyDescription: string;
    vacancyCompanyDescription: string;
    hhVacancyId: string | number | null;
    positionId: string | number | null;
    recruiterName: string;
    recruiterId: string | undefined;
  } | null>(null);

  // TASKNEIROKLYUCH-465: вместо немедленного запуска по N кампаний создаём
  // одну DRAFT-рассылку со всеми кандидатами и переводим рекрутера в окно
  // настроек рассылки — там он редактирует первое сообщение и жмёт «Запустить».
  const finalizeClarifyCampaign = async (
    candidates: any[],
    candidateResponseIds: string[],
    skippedNoResume: number,
    skippedNoContacts: number,
    skippedContactOpen: number,
    skippedEstaffSync: number,
    ctx: {
      estaffVacancyId: string | number | null;
      vacancyTitle: string;
      vacancyDescription: string;
      vacancyCompanyDescription: string;
      hhVacancyId: string | number | null;
      positionId: string | number | null;
      recruiterName: string;
      recruiterId: string | undefined;
    },
  ) => {
    if (candidates.length === 0) {
      toast.error('Не удалось получить ID резюме у выбранных кандидатов');
      return;
    }

    const campaignName = candidates.length === 1
      ? `Уточнение опыта: ${candidates[0].name}`
      : `Уточнение опыта: ${ctx.vacancyTitle} (${candidates.length})`;

    let campaignSettings = {};
    try {
      campaignSettings = await loadLatestCampaignReusableSettings(ctx.positionId);
    } catch (error) {
      console.warn('Failed to load previous campaign settings, using defaults', error);
    }

    const campaignData = buildClarifyExperienceCampaignData({
      candidates,
      vacancyTitle: ctx.vacancyTitle,
      vacancyDescription: ctx.vacancyDescription,
      vacancyCompanyDescription: ctx.vacancyCompanyDescription,
      hhVacancyId: ctx.hhVacancyId || undefined,
      estaffVacancyId: ctx.estaffVacancyId || undefined,
      positionId: ctx.positionId || undefined,
      recruiterName: ctx.recruiterName,
      recruiterId: ctx.recruiterId,
      aiScreeningEnabled: Boolean((selectedVacancy as any)?.aiScreeningEnabled),
      hrInterviewEnabled: (selectedVacancy as any)?.hrInterviewEnabled !== false,
      campaignName,
      campaignSettings,
    });

    const companyId = useAuthStore.getState().user?.companyId;
    const requestConfig = companyId ? { companyId } : undefined;
    const created = await createCampaign(campaignData, requestConfig);

    if (skippedNoResume > 0) {
      toast.error(`Пропущено откликов без ID резюме: ${skippedNoResume}`);
    }
    if (skippedNoContacts > 0) {
      toast.error(`Пропущено откликов без телефона или email: ${skippedNoContacts}`);
    }
    if (skippedContactOpen > 0) {
      toast.error(`Пропущено откликов, по которым не удалось открыть контакты: ${skippedContactOpen}`);
    }
    if (skippedEstaffSync > 0) {
      toast.error(`Пропущено откликов без постановки в E-Staff sync: ${skippedEstaffSync}`);
    }
    toast.success('Рассылка создана. Проверьте настройки и нажмите «Запустить».');

    onRemoveResponses?.(candidateResponseIds);
    onSelectedResponsesChange(new Set());

    navigate('/recruiter/mailings', {
      state: {
        [MAILINGS_NAV_STATE_KEY]: {
          campaignId: String(created.id),
          activeTab: 'settings',
        },
      },
    });
  };

  const handleBulkClarifyExperience = async (responsesOverride?: any[]) => {
    if (!responsesOverride && selectedResponses.size === 0) {
      toast.error('Выберите кандидатов');
      return;
    }

    const selected = responsesOverride ?? getSelectedResponsesList();
    if (selected.length === 0) return;

    setBulkClarifyingInProgress(true);

    const user = useAuthStore.getState().user;
    const tenantUserId = user?.id;
    // Только имя (без фамилии): подставляется как {{recruiter_name}} в первое
    // сообщение и в поле «Имя AI-рекрутера» настроек рассылки. В холодном поиске
    // recruiterName уходит пустым и бэк подтягивает короткое имя из настроек
    // мессенджер-аккаунта — тут оставляем тот же стиль («Виктор», не «Виктор Феско»).
    const recruiterName = user?.firstName || 'Рекрутер';
    const vacancyTitle = selectedVacancy?.title || 'Вакансия';
    const vacancyDescription = selectedVacancy?.description || '';
    const vacancyCompanyDescription = selectedVacancy?.companyDescription || '';
    const positionId = useVacancyStore.getState().selectedVacancyId;
    const hhVacancyId = (selectedVacancy as any)?.hhVacancyId;

    try {
      // Проверяем E-Staff source-link без открытия отдельной модалки.
      // Предупреждение имеет смысл только если E-Staff включён у компании.
      const estaffLink = await checkEstaffLinkStatus(positionId);
      const estaffVacancyId = estaffLink.estaffVacancyId;
      const estaffMissingMessage = estaffLink.estaffEnabled && !estaffVacancyId
        ? 'Для этой позиции не выбрана вакансия E-Staff. Контакты открыты в HH, но кандидаты не будут сохранены в E-Staff.'
        : null;

      const candidates: any[] = [];
      const candidateResponseIds: string[] = [];
      let skippedNoResume = 0;
      let skippedNoContacts = 0;
      let skippedContactOpen = 0;
      let skippedEstaffSync = 0;
      for (const response of selected) {
        const resume = response.resume || {};
        if (!resume.id) {
          skippedNoResume++;
          continue;
        }

        // Снапшот AI-оценки отклика: API возвращает плоскую структуру
        // { rating, comment, positiveFacts, negativeFacts }. evaluationToRelevanceInput
        // приведёт её к тому же виду aiComment, что приходит из холодного поиска,
        // чтобы RelevanceBlock в диалоге рисовал плюсы/минусы (TASKNEIROKLYUCH-728).
        const responseVacancyId = response?.sourceVacancyId || hhVacancyId;
        const evaluation = responseVacancyId
          ? aiEvaluations[`${responseVacancyId}_${response.id}`]
          : undefined;
        const relevanceInput = evaluationToRelevanceInput(evaluation);
        const relevanceFields = relevanceInput ? buildRelevanceFields(relevanceInput) : {};

        let fullResume: any = null;
        let campaignCandidate: any = null;
        try {
          const opened = await openHHResumeContacts(resume.id, {
            canViewContacts: resume.canViewContacts,
            vacancyId: estaffVacancyId || undefined,
          });
          fullResume = opened?.resume || opened?.data || null;
          campaignCandidate = opened?.campaignCandidate || opened?.data?.campaignCandidate || null;
          const hasOpenedContacts = Boolean(
            opened?.hasContacts
            || (Array.isArray(fullResume?.contact) && fullResume.contact.length > 0)
          );
          if (!hasOpenedContacts) {
            skippedNoContacts++;
            continue;
          }
          if (!isOpenedContactsReadyForClarify(opened, estaffVacancyId)) {
            skippedEstaffSync++;
            continue;
          }
        } catch (_err) {
          skippedContactOpen++;
          continue;
        }

        const { candidate } = buildClarifyExperienceCandidate({
          resume,
          fullResume,
          campaignCandidate,
          response,
          responseId: response.id,
          relevanceFields,
        });
        if (!hasClarifyCandidateContact(candidate)) {
          skippedNoContacts++;
          continue;
        }
        candidates.push(candidate);
        candidateResponseIds.push(String(response.id));
      }

      if (candidates.length === 0) {
        toast.error(
          skippedContactOpen > 0
            ? 'Не удалось открыть контакты выбранных откликов. Рассылка не создана.'
            : skippedEstaffSync > 0
            ? 'Не удалось поставить выбранные отклики в очередь синхронизации E-Staff. Рассылка не создана.'
            : skippedNoContacts > 0
            ? 'У выбранных откликов нет телефона или email. Рассылка не создана.'
            : 'Не удалось получить ID резюме у выбранных кандидатов',
        );
        return;
      }

      const ctx = {
        estaffVacancyId: estaffVacancyId || null,
        vacancyTitle,
        vacancyDescription,
        vacancyCompanyDescription,
        hhVacancyId: hhVacancyId || null,
        positionId: positionId || null,
        recruiterName,
        recruiterId: tenantUserId ? String(tenantUserId) : undefined,
      };

      // TASKNEIROKLYUCH-434: перед стартом clarify-experience диалога — проверяем
      // занятость кандидата в e-staff и наш «писали последний месяц». Если кто-то
      // блокирован — показываем модалку с ФИО заблокированных и даём выбор:
      // запустить только с доступными или отменить и вернуться к выбору откликов.
      const companyId = useAuthStore.getState().user?.companyId || null;
      const candidatesForCheck = candidates.map((c) => ({
        name: c.name,
        phone: c.messengerContacts?.phone || null,
        email: c.email || c.messengerContacts?.email || null,
        hhResumeId: c.messengerContacts?.hhResumeId || null,
      }));
      toast.loading('Проверяем доступность кандидатов...', { id: 'checking-candidates' });
      let result: any = null;
      try {
        result = await checkCampaignCandidates(candidatesForCheck, companyId, positionId);
      } catch (err) {
        toast.dismiss('checking-candidates');
        toast('Не удалось проверить статус кандидатов в E-Staff. Рассылка будет создана без проверки.', { icon: '⚠️' });
      }
      toast.dismiss('checking-candidates');

      const { available: filteredPairs, hasBlocked } =
        partitionClarifyCandidatesByCheck(candidates, candidateResponseIds, result || {});
      if (hasBlocked || estaffMissingMessage) {
        if (filteredPairs.length === 0) {
          toast.error(
            `Все ${candidates.length} кандидат(ов) в активном процессинге E-Staff или мы писали им за последний месяц — рассылка не создана.`,
          );
          return;
        }
        setCheckResult(result || { total: candidates.length, available: filteredPairs.length, blocked: [] });
        setCheckResultMessage(estaffMissingMessage);
        setPendingCampaign({
          candidates: filteredPairs.map((p) => p.candidate),
          candidateResponseIds: filteredPairs.map((p) => p.responseId),
          skippedNoResume,
          skippedNoContacts,
          skippedContactOpen,
          skippedEstaffSync,
          ...ctx,
        });
        setCheckResultsModalOpen(true);
        return;
      }

      await finalizeClarifyCampaign(candidates, candidateResponseIds, skippedNoResume, skippedNoContacts, skippedContactOpen, skippedEstaffSync, ctx);
    } catch (error: any) {
      console.error('Error creating clarify experience campaign:', error);
      toast.error(error?.response?.data?.message || 'Ошибка при создании рассылки');
    } finally {
      setBulkClarifyingInProgress(false);
    }
  };

  const prepareAvitoResponsesForCampaign = async (responses: any[], mode: 'regular' | 'clarify') => {
    const candidates: any[] = [];
    const applicationIds: string[] = [];
    let skippedNoResume = 0;
    let skippedNoChat = 0;

    for (const response of responses) {
      const applicationId = getAvitoApplicationId(response);
      const resumeId = getAvitoResumeId(response);
      if (!applicationId || !resumeId) {
        skippedNoResume++;
        continue;
      }

      const chatId = getAvitoChatId(response);
      const phone = getAvitoPhone(response);

      if (!chatId) {
        skippedNoChat++;
        continue;
      }

      const responseVacancyId = response?.sourceVacancyId || (selectedVacancy as any)?.avitoVacancyId;
      const evaluation = responseVacancyId
        ? aiEvaluations[`${responseVacancyId}_${response.id}`]
        : undefined;
      const relevanceInput = evaluationToRelevanceInput(evaluation);
      const relevanceFields = relevanceInput ? buildRelevanceFields(relevanceInput) : {};

      if (mode === 'clarify') {
        const { candidate } = buildClarifyExperienceCandidate({
          source: 'avito',
          resume: {
            ...(response.resume || {}),
            id: resumeId,
            avitoChatId: chatId,
          },
          campaignCandidate: {
            avitoChatId: chatId,
            phone,
          },
          response,
          responseId: applicationId,
          avitoChatId: chatId,
          avitoApplicationId: applicationId,
          resumeUrl: response?.resume?.resumeUrl || response?.resume?.url,
          relevanceFields,
        });
        if (!hasClarifyCandidateContact(candidate)) {
          skippedNoChat++;
          continue;
        }
        candidates.push(candidate);
      } else {
        candidates.push(buildAvitoResponseCampaignCandidate(response, {
          avitoChatId: chatId,
          phone,
        }));
      }
      applicationIds.push(applicationId);
    }

    return {
      candidates,
      applicationIds,
      skippedNoResume,
      skippedNoChat,
      skippedContactOpen: 0,
    };
  };

  const applyAvitoMaybeSuitableStatus = async (applicationIds: string[]) => {
    let states = avitoApplicationStates;
    if (states.length === 0) {
      try {
        states = await getAvitoApplicationStates();
        setAvitoApplicationStates(Array.isArray(states) ? states : []);
      } catch (_err) {
        states = [];
      }
    }
    const action = findAvitoMaybeSuitableStatus(states);
    if (!action) {
      toast('Рассылка создана, но статус не изменён: в Avito-статусах не найдено «Может подойти».', { icon: '⚠️' });
      return;
    }
    try {
      await applyAvitoApplicationAction(applicationIds, action);
    } catch (error: any) {
      toast(error?.response?.data?.message || 'Рассылка создана, но статус Avito-откликов не изменился.', { icon: '⚠️' });
    }
  };

  const handleAvitoApplySelectedStatus = async () => {
    if (!selectedAvitoAction) {
      toast.error('Выберите статус Avito');
      return;
    }
    const selected = getSelectedResponsesList();
    const applicationIds = selected.map(getAvitoApplicationId).filter((id): id is string => Boolean(id));
    if (applicationIds.length === 0) {
      toast.error('Не удалось получить ID Avito-откликов');
      return;
    }

    setAvitoBulkActionInProgress(true);
    try {
      await applyAvitoApplicationAction(applicationIds, selectedAvitoAction);
      toast.success('Статус Avito-откликов изменён');
      onSelectedResponsesChange(new Set());
      onRefresh();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Не удалось изменить статус Avito-откликов');
    } finally {
      setAvitoBulkActionInProgress(false);
    }
  };

  const handleAvitoCreateCampaign = async (mode: 'regular' | 'clarify') => {
    if (selectedResponses.size === 0) {
      toast.error('Выберите кандидатов');
      return;
    }
    const selected = getSelectedResponsesList();
    if (selected.length === 0) return;

    setAvitoMailingInProgress(true);
    try {
      const prepared = await prepareAvitoResponsesForCampaign(selected, mode);
      if (prepared.candidates.length === 0) {
        if (prepared.skippedContactOpen > 0) {
          toast.error('Не удалось открыть контакты Avito. Рассылка не создана.');
        } else if (prepared.skippedNoChat > 0) {
          toast.error('У выбранных Avito-откликов нет chat_id. Рассылка не создана.');
        } else {
          toast.error('Не удалось получить ID резюме у выбранных Avito-откликов');
        }
        return;
      }

      if (prepared.skippedNoResume > 0) {
        toast.error(`Пропущено Avito-откликов без ID резюме: ${prepared.skippedNoResume}`);
      }
      if (prepared.skippedNoChat > 0) {
        toast.error(`Пропущено Avito-откликов без chat_id: ${prepared.skippedNoChat}`);
      }
      if (prepared.skippedContactOpen > 0) {
        toast.error(`Пропущено Avito-откликов, по которым не удалось открыть контакты: ${prepared.skippedContactOpen}`);
      }

      const user = useAuthStore.getState().user;
      const positionId = useVacancyStore.getState().selectedVacancyId;
      const vacancyTitle = selectedVacancy?.title || 'Вакансия';
      const recruiterName = user?.firstName || 'Рекрутер';
      const campaignName = mode === 'clarify'
        ? `Уточнение опыта: ${vacancyTitle} (${prepared.candidates.length})`
        : `Рассылка: ${vacancyTitle} (${prepared.candidates.length})`;
      const requestConfig = user?.companyId ? { companyId: user.companyId } : undefined;
      if (user?.companyId) {
        const creditsCheck = await checkCampaignCreationCredits(
          user.companyId,
          prepared.candidates.length,
          Boolean((selectedVacancy as any)?.aiScreeningEnabled),
          positionId,
        );
        if (creditsCheck.status === 'ERROR') {
          toast.error(creditsCheck.message || 'Недостаточно доступных диалогов для создания рассылки.');
          return;
        }
        if (creditsCheck.status === 'WARNING') {
          toast(creditsCheck.message || 'Рассылка будет создана, но запуск может быть ограничен лимитами.', {
            icon: '⚠️',
            duration: 8000,
          });
        }
      }

      const campaignData = mode === 'clarify'
        ? buildClarifyExperienceCampaignData({
            source: 'avito',
            candidates: prepared.candidates,
            vacancyTitle,
            vacancyDescription: selectedVacancy?.description || '',
            vacancyCompanyDescription: selectedVacancy?.companyDescription || '',
            avitoVacancyId: (selectedVacancy as any)?.avitoVacancyId,
            positionId: positionId || undefined,
            recruiterName,
            recruiterId: user?.id ? String(user.id) : undefined,
            aiScreeningEnabled: Boolean((selectedVacancy as any)?.aiScreeningEnabled),
            hrInterviewEnabled: (selectedVacancy as any)?.hrInterviewEnabled !== false,
            campaignName,
          })
        : buildAvitoResponsesCampaignData({
            candidates: prepared.candidates,
            selectedVacancy,
            positionId: positionId || undefined,
            recruiterId: user?.id ? String(user.id) : undefined,
            campaignName,
            domain: window.location.hostname,
          });

      const created = await createCampaign(campaignData, requestConfig);
      await applyAvitoMaybeSuitableStatus(prepared.applicationIds);

      toast.success('Рассылка создана. Проверьте настройки и нажмите «Запустить».');
      onRemoveResponses?.(prepared.applicationIds);
      onSelectedResponsesChange(new Set());
      onRefresh();

      navigate('/recruiter/mailings', {
        state: {
          [MAILINGS_NAV_STATE_KEY]: {
            campaignId: String(created.id),
            activeTab: 'settings',
          },
        },
      });
    } catch (error: any) {
      console.error('Error creating Avito responses campaign:', error);
      toast.error(error?.response?.data?.message || 'Ошибка при создании рассылки');
    } finally {
      setAvitoMailingInProgress(false);
    }
  };

  // TASKNEIROKLYUCH-434: подтверждение модалки → создаём рассылку только с доступными.
  const handleCheckConfirm = async () => {
    if (!pendingCampaign) return;
    const pending = pendingCampaign;
    setCheckResultsModalOpen(false);
    setPendingCampaign(null);
    setCheckResult(null);
    setCheckResultMessage(null);
    setBulkClarifyingInProgress(true);
    try {
      await finalizeClarifyCampaign(
        pending.candidates,
        pending.candidateResponseIds,
        pending.skippedNoResume,
        pending.skippedNoContacts,
        pending.skippedContactOpen,
        pending.skippedEstaffSync,
        {
          estaffVacancyId: pending.estaffVacancyId,
          vacancyTitle: pending.vacancyTitle,
          vacancyDescription: pending.vacancyDescription,
          vacancyCompanyDescription: pending.vacancyCompanyDescription,
          hhVacancyId: pending.hhVacancyId,
          positionId: pending.positionId,
          recruiterName: pending.recruiterName,
          recruiterId: pending.recruiterId,
        },
      );
    } catch (error: any) {
      console.error('Error creating clarify experience campaign (after check):', error);
      toast.error(error?.response?.data?.message || 'Ошибка при создании рассылки');
    } finally {
      setBulkClarifyingInProgress(false);
    }
  };

  const handleCheckCancel = () => {
    setCheckResultsModalOpen(false);
    setPendingCampaign(null);
    setCheckResult(null);
    setCheckResultMessage(null);
  };

  const getAutoParsePool = () => {
    const poolRaw = allResponsesData ?? responsesData;
    return Array.isArray(poolRaw) ? poolRaw : [];
  };

  const getLimitedAutoParsePool = () => {
    const limit = Math.min(500, Math.max(1, Number.isFinite(responsesLoadLimit) ? Math.floor(responsesLoadLimit) : 100));
    return getAutoParsePool().slice(0, limit);
  };

  const getAutoParseAvailableCount = () => Math.min(500, getAutoParsePool().length);

  const loadAutoParseTemplates = async (pool = getLimitedAutoParsePool()) => {
    setAutoParseTemplatesLoading(true);
    try {
      const templates = await loadAutoParseActionTemplates(pool);
      setAutoParseInviteTemplateId(templates.inviteTemplateId);
      setAutoParseRejectTemplateId(templates.rejectTemplateId);
      setAutoParseInviteMessage(templates.inviteMessage);
      setAutoParseRejectMessage(templates.rejectMessage);
    } finally {
      setAutoParseTemplatesLoading(false);
    }
  };

  const runAutoParse = async () => {
    if (!isHhResponsesSource) return;

    const hhVacancyId = (selectedVacancy as any)?.hhVacancyId;
    const { grouped, skippedWithoutRating } = groupResponsesForAutoParse(
      getLimitedAutoParsePool(),
      aiEvaluations,
      hhVacancyId,
      autoParseConfig,
    );

    const total = grouped.invite.length + grouped.clarify.length + grouped.reject.length;
    if (total === 0) {
      toast.error('Нет откликов с рассчитанной релевантностью для авторазбора');
      return;
    }

    setAutoParseRunning(true);
    autoParseStopRequestedRef.current = false;

    try {
      const removedResponseIds: string[] = [];

      if (grouped.invite.length > 0) {
        const result = await sendAutoParseHHAction({
          responses: grouped.invite,
          actionType: 'invite',
          message: autoParseInviteMessage,
          templateId: autoParseInviteTemplateId,
          fallbackVacancyId: hhVacancyId,
          positionId: (selectedVacancy as any)?.id ?? null,
          shouldStop: () => autoParseStopRequestedRef.current,
        });
        removedResponseIds.push(...result.successfulResponseIds);
        if (result.successCount > 0) toast.success(`Приглашено: ${result.successCount}`);
        if (result.errorCount > 0) toast.error(`Ошибки приглашения: ${result.errorCount}`);
      }

      if (!autoParseStopRequestedRef.current && grouped.reject.length > 0) {
        const result = await sendAutoParseHHAction({
          responses: grouped.reject,
          actionType: 'reject',
          message: autoParseRejectMessage,
          templateId: autoParseRejectTemplateId,
          fallbackVacancyId: hhVacancyId,
          positionId: (selectedVacancy as any)?.id ?? null,
          shouldStop: () => autoParseStopRequestedRef.current,
        });
        removedResponseIds.push(...result.successfulResponseIds);
        if (result.successCount > 0) toast.success(`Отказ отправлен: ${result.successCount}`);
        if (result.errorCount > 0) toast.error(`Ошибки отказа: ${result.errorCount}`);
      }

      if (removedResponseIds.length > 0) {
        onRemoveResponses?.(removedResponseIds);
      }

      if (!autoParseStopRequestedRef.current && grouped.clarify.length > 0) {
        await handleBulkClarifyExperience(grouped.clarify);
        return;
      }

      if (skippedWithoutRating > 0) {
        toast.error(`Пропущено без оценки релевантности: ${skippedWithoutRating}`);
      }

      if (autoParseStopRequestedRef.current) {
        toast('Авторазбор остановлен');
      } else {
        toast.success('Авторазбор завершён');
      }
      onRefresh();
    } finally {
      setAutoParseRunning(false);
      autoParseStopRequestedRef.current = false;
    }
  };

  const handleStartAutoParse = () => {
    if (!isHhResponsesSource) return;

    if (autoParseRunning) {
      autoParseStopRequestedRef.current = true;
      toast('Останавливаем авторазбор...');
      return;
    }

    const availableCount = getAutoParseAvailableCount();
    const nextLimit = Math.max(1, availableCount);
    onResponsesLoadLimitChange?.(nextLimit);

    const counts = getAutoParseBucketCounts(getAutoParsePool().slice(0, nextLimit), aiEvaluations, (selectedVacancy as any)?.hhVacancyId);
    const total = Object.values(counts).reduce((sum, count) => sum + count, 0);
    if (total === 0) {
      toast.error('Сначала дождитесь оценки релевантности откликов');
      return;
    }

    setAutoParseModalOpen(true);
    void loadAutoParseTemplates(getAutoParsePool().slice(0, nextLimit));
  };

  const handleOrderChange = (value: string) => {
    if (!onFiltersChange) return;
    onFiltersChange({ orderBy: value });
    setOrderDropdownOpen(false);
  };

  const handleCollectionChange = (value: string) => {
    if (!onFiltersChange) return;
    onFiltersChange({ collection: value });
    setCollectionDropdownOpen(false);
  };

  const handleExperienceChange = (values: {
    totalExperience: string | string[];
    positionExperience: string | string[];
    experiencePeriod: string;
  }) => {
    if (!onFiltersChange) return;
    const experience = Array.isArray(values.totalExperience) 
      ? values.totalExperience 
      : values.totalExperience ? [values.totalExperience] : [];
    onFiltersChange({ experience });
  };

  const handleAreaChange = (areas: string[]) => {
    if (!onFiltersChange) return;
    onFiltersChange({ areaIds: areas });
  };

  const selectedCount = selectedResponses.size;
  const autoParseBucketCounts = isHhResponsesSource
    ? getAutoParseBucketCounts(getLimitedAutoParsePool(), aiEvaluations, (selectedVacancy as any)?.hhVacancyId)
    : {};

  const pluralizeResponses = (n: number) => {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return `${n} отклик`;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${n} отклика`;
    return `${n} откликов`;
  };

  const currentPage = pagination?.currentPage ?? 1;
  const perPage = pagination?.perPage ?? 10;
  const totalItems = pagination?.totalItems ?? responsesData.length;
  const totalPages = pagination?.totalPages ?? 1;
  const pageIndex = Math.max(0, currentPage - 1);
  const pageNumbers = generatePageNumbers(currentPage, totalPages);

  const getExperienceLabel = () => {
    if (!filters?.experience || filters.experience.length === 0) return 'Опыт';
    if (filters.experience.length === 1) {
      const opt = RESPONSES_EXPERIENCE_OPTIONS.find(o => o.value === filters.experience[0]);
      return opt?.label || 'Опыт';
    }
    return `Опыт (${filters.experience.length})`;
  };

  const getAgeLabel = () => {
    if (!filters) return 'Возраст';
    if (filters.ageFrom && filters.ageTo) return `${filters.ageFrom}-${filters.ageTo} лет`;
    if (filters.ageFrom) return `от ${filters.ageFrom} лет`;
    if (filters.ageTo) return `до ${filters.ageTo} лет`;
    return 'Возраст';
  };

  const getOrderLabel = () => {
    if (!filters?.orderBy) return 'По дате обновления';
    const opt = RESPONSES_ORDER_OPTIONS.find(o => o.value === filters.orderBy);
    return opt?.label || 'По дате обновления';
  };

  const isAvitoApplicationStateChanged = () => (
    (filters?.avitoApplicationState || DEFAULT_AVITO_APPLICATION_STATE) !== DEFAULT_AVITO_APPLICATION_STATE
  );

  const getAvitoApplicationStateLabel = () => {
    const value = filters?.avitoApplicationState || DEFAULT_AVITO_APPLICATION_STATE;
    if (value === ALL_AVITO_APPLICATION_STATES) return 'Все статусы';
    const state = avitoApplicationStates.find((item: any) => item?.id === value || item?.slug === value);
    if (state) return state.name || state.description || state.id || value;
    if (value === DEFAULT_AVITO_APPLICATION_STATE) return 'Новый кандидат';
    return value;
  };

  const getAvitoStatusFilterLabel = () => (
    isAvitoApplicationStateChanged() ? getAvitoApplicationStateLabel() : 'Статус'
  );

  const getAvitoStatusOptions = () => {
    const loadedOptions = avitoApplicationStates
      .map((state: any) => ({
        value: String(state?.id || state?.slug || '').trim(),
        label: state?.name || state?.description || state?.id || state?.slug || '',
      }))
      .filter((state: any) => state.value && state.label);
    const hasDefaultStatus = loadedOptions.some((state: any) => state.value === DEFAULT_AVITO_APPLICATION_STATE);

    return [
      { value: ALL_AVITO_APPLICATION_STATES, label: 'Все статусы' },
      ...(hasDefaultStatus ? [] : [{ value: DEFAULT_AVITO_APPLICATION_STATE, label: 'Новый кандидат' }]),
      ...loadedOptions,
    ];
  };

  const handleAvitoStatusChange = (value: string) => {
    if (!onFiltersChange) return;
    onFiltersChange({ avitoApplicationState: value });
    setAvitoStatusDropdownOpen(false);
  };

  const getCollectionLabel = () => {
    const value = filters?.collection || 'response';
    if (value === 'response') return 'Коллекция';
    const opt = EMPLOYER_STATES_OPTIONS.find(o => o.value === value);
    return opt?.label || 'Коллекция';
  };

  const [isSalaryPopupOpen, setIsSalaryPopupOpen] = useState(false);
  const [salaryPopupPosition, setSalaryPopupPosition] = useState({ top: 0, left: 0 });
  const salaryButtonRef = useRef<HTMLDivElement>(null);

  const setPreset = (preset: 'high' | 'medium' | 'low' | null) => {
    if (onRelevancePresetChange) onRelevancePresetChange(preset);
  };

  const setCount = (count: number) => {
    if (onRelevanceCountChange) onRelevanceCountChange(count);
  };

  const openResponsesFilters = (section: ResponsesFiltersInitialSection | null = null) => {
    setFiltersInitialSection(section);
    setIsFiltersModalOpen(true);
  };

  const closeResponsesFilters = () => {
    setIsFiltersModalOpen(false);
    setFiltersInitialSection(null);
  };

  const getSalaryLabel = () => {
    if (!filters) return 'Зарплата';
    if (filters.salaryFrom && filters.salaryTo) return `Зарплата (${filters.salaryFrom} – ${filters.salaryTo})`;
    if (filters.salaryFrom) return `Зарплата (от ${filters.salaryFrom})`;
    if (filters.salaryTo) return `Зарплата (до ${filters.salaryTo})`;
    return 'Зарплата';
  };

  const handleOpenSalaryPopup = () => {
    if (salaryButtonRef.current) {
      const rect = salaryButtonRef.current.getBoundingClientRect();
      setSalaryPopupPosition({ top: rect.bottom + 8, left: rect.left });
    }
    setIsSalaryPopupOpen(true);
  };

  return (
    <div className="px-2 space-y-4">
      <div className="bg-white rounded-2xl p-6 flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-5">
            <h3 className="text-lg font-semibold text-[#111827]">{pluralizeResponses(totalItems)}</h3>
            {isHhResponsesSource && <div className="w-px h-[22px] bg-[#CDD0D5]" />}
            {isHhResponsesSource && <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setParseMode('manual')}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[13px] font-medium transition-colors ${
                  parseMode === 'manual'
                    ? 'bg-white border border-[#161922] text-[#161922]'
                    : 'border border-transparent text-[#525866] hover:bg-gray-50'
                }`}
              >
                <img 
                  src={IconManualParse} 
                  alt="" 
                  className="w-3.5 h-3.5"
                  style={{ filter: parseMode === 'manual' ? 'none' : 'brightness(0) saturate(100%) invert(35%) sepia(6%) saturate(740%) hue-rotate(182deg) brightness(94%) contrast(89%)' }}
                />
                Ручной разбор
              </button>
              <button
                type="button"
                onClick={() => setParseMode('auto')}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[13px] font-medium transition-colors ${
                  parseMode === 'auto'
                    ? 'bg-white border border-[#161922] text-[#161922]'
                    : 'border border-transparent text-[#525866] hover:bg-gray-50'
                }`}
              >
                <img 
                  src={IconAutoParse} 
                  alt="" 
                  className="w-3.5 h-3.5"
                  style={{ filter: parseMode === 'auto' ? 'brightness(0) saturate(100%) invert(8%) sepia(10%) saturate(1173%) hue-rotate(182deg) brightness(96%) contrast(92%)' : 'none' }}
                />
                Авторазбор
              </button>
            </div>}
          </div>
          <div className="flex items-center gap-2">
            {(isHhResponsesSource || isAvitoResponsesSource) && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    handleClearFilters();
                    setPreset(null);
                  }}
                  className="h-11 px-5 rounded-xl border border-[#E2E4E9] bg-white text-[15px] font-medium text-[#0A0D14] hover:bg-gray-50 transition-colors"
                >
                  Сбросить фильтры
                </button>
                <button
                  type="button"
                  onClick={() => openResponsesFilters()}
                  className={`h-11 px-5 rounded-xl border text-[15px] font-medium transition-colors flex items-center gap-2 ${
                    showFilters 
                      ? 'border-[#E2E4E9] bg-[#F6F8FA] text-[#0A0D14]' 
                      : 'border-[#E2E4E9] bg-white text-[#0A0D14] hover:bg-gray-50'
                  }`}
                >
                  <FunnelIcon className="w-5 h-5" />
                  Фильтры
                </button>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {(isHhResponsesSource || isAvitoResponsesSource) && (
              <button
                type="button"
                onClick={() => {
                  const selectableResponses = responsesData.filter((r) => {
                    const source = (r?.sourceType || r?.source || r?.resume?.source || '').toString().toLowerCase();
                    return isAvitoResponsesSource ? source === 'avito' : source !== 'avito';
                  });
                  const currentPageIds = new Set(selectableResponses.map(r => r.id));
                  const allSelected = selectableResponses.length > 0 && selectableResponses.every(r => selectedResponses.has(r.id));
                  if (allSelected) {
                    const newSelected = new Set(selectedResponses);
                    currentPageIds.forEach(id => newSelected.delete(id));
                    onSelectedResponsesChange(newSelected);
                  } else {
                    const newSelected = new Set(selectedResponses);
                    currentPageIds.forEach(id => newSelected.add(id));
                    onSelectedResponsesChange(newSelected);
                  }
                }}
                className="relative box-border flex h-11 shrink-0 content-stretch items-center justify-center rounded-[12px] bg-white px-4 hover:bg-gray-50 transition-colors"
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 rounded-[12px] border border-solid border-[#e2e4e9]"
                />
                <span className="relative font-inter text-[14px] font-normal text-black whitespace-nowrap">
                  {responsesData.some((r) => {
                    const source = (r?.sourceType || r?.source || r?.resume?.source || '').toString().toLowerCase();
                    return isAvitoResponsesSource ? source === 'avito' : source !== 'avito';
                  })
                    && responsesData
                      .filter((r) => {
                        const source = (r?.sourceType || r?.source || r?.resume?.source || '').toString().toLowerCase();
                        return isAvitoResponsesSource ? source === 'avito' : source !== 'avito';
                      })
                      .every(r => selectedResponses.has(r.id))
                    ? 'Снять выбор'
                    : 'Выбрать все'}
                </span>
              </button>
            )}
            <div className="relative h-11 w-[400px] shrink-0 rounded-[12px] bg-white">
              <div className="relative box-border flex h-11 w-[400px] content-stretch items-center gap-[8px] overflow-clip p-[12px]">
                <div className="relative size-[20px] shrink-0 overflow-clip">
                  <MagnifyingGlassIcon className="h-5 w-5 text-[#525866]" />
                </div>
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                  placeholder="Поиск по кандидатам"
                  className="relative min-h-px min-w-px basis-0 grow shrink-0 border-none bg-transparent font-inter text-[16px] font-normal not-italic leading-[0] tracking-[-0.084px] text-[#525866] outline-none"
                  style={{ lineHeight: '22px' }}
                />
              </div>
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 rounded-[12px] border border-solid border-[#cdd0d5]"
              />
            </div>
            {isHhResponsesSource && <div className="relative">
              <button
                ref={orderButtonRef}
               onClick={() => !(responsesRelevanceApplied || responsesRelevanceLoading) && setOrderDropdownOpen(!orderDropdownOpen)}
                disabled={responsesRelevanceApplied || responsesRelevanceLoading}
                className={`relative box-border flex h-11 shrink-0 content-stretch items-center justify-center rounded-[12px] bg-white p-[12px] transition-colors ${(responsesRelevanceApplied || responsesRelevanceLoading) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 rounded-[12px] border border-solid border-[#e2e4e9]"
                />
                <div className="relative flex shrink-0 flex-col justify-center font-inter text-[14px] font-normal not-italic leading-[0] text-black">
                  <p className="whitespace-nowrap leading-[20px]">{(responsesRelevanceApplied || responsesRelevanceLoading) ? 'По AI-рейтингу' : getOrderLabel()}</p>
                </div>
                <ChevronDownIcon className="w-4 h-4 ml-2 text-[#868C98]" />
              </button>
              {orderDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-[9998]" onClick={() => setOrderDropdownOpen(false)} />
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 p-3 z-[9999] min-w-[220px]">
                    <div className="space-y-2">
                      {RESPONSES_ORDER_OPTIONS.map(option => (
                        <label key={option.value} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg">
                          <input
                            type="radio"
                            name="orderBy"
                            checked={filters?.orderBy === option.value}
                            onChange={() => handleOrderChange(option.value)}
                            className="w-4 h-4 text-[#E16349] border-gray-300 focus:ring-[#E16349]"
                          />
                          <span className="text-sm text-[#0A0D14]">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>}
            {pagination && (
              <div className="relative">
                <select
                  value={perPage}
                  onChange={(e) => pagination.onPerPageChange(parseInt(e.target.value, 10))}
                  className="h-11 px-4 pr-8 rounded-[12px] border border-[#e2e4e9] bg-white text-[14px] text-black appearance-none cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  {[10, 20, 50].map((v) => (
                    <option key={v} value={v}>{v} резюме</option>
                  ))}
                </select>
                <ChevronDownIcon className="w-4 h-4 text-[#868C98] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            )}
            <button
              onClick={handleSearch}
              className="relative box-border flex h-11 shrink-0 content-stretch items-center gap-[8px] rounded-[12px] bg-[#E16349] px-[32px] py-[14px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] hover:bg-[#c9573f] transition-colors"
            >
              <div className="relative flex h-[17px] shrink-0 flex-col justify-center font-inter text-[14px] font-medium not-italic leading-[0] text-white">
                <p className="leading-[20px]">Найти</p>
              </div>
            </button>
          </div>
        </div>

        {isHhResponsesSource && <div className="flex flex-wrap items-center gap-[8px]">
          <div className="flex flex-wrap items-center gap-[8px]">
            <FilterButton
              label={getExperienceLabel()}
              isActive={!!(filters?.experience && filters.experience.length > 0)}
              onClick={() => setIsExperienceModalOpen(true)}
              onRemove={onFiltersChange && (filters?.experience?.length ?? 0) > 0 ? () => onFiltersChange({ experience: [] }) : undefined}
              showRemove={!!(filters?.experience && filters.experience.length > 0)}
            />
            <div ref={ageButtonRef as any}>
              <FilterButton
                label={getAgeLabel()}
                isActive={!!(filters?.ageFrom || filters?.ageTo)}
                onClick={handleOpenAgePopup}
                onRemove={onFiltersChange && (filters?.ageFrom || filters?.ageTo) ? () => onFiltersChange({ ageFrom: '', ageTo: '' }) : undefined}
                showRemove={!!(filters?.ageFrom || filters?.ageTo)}
              />
            </div>
            <FilterButton
              label={filters?.areaIds && filters.areaIds.length > 0 ? `Локация (${filters.areaIds.length})` : 'Локация'}
              isActive={!!(filters?.areaIds && filters.areaIds.length > 0)}
              onClick={() => setIsAreaModalOpen(true)}
              onRemove={onFiltersChange && (filters?.areaIds?.length ?? 0) > 0 ? () => onFiltersChange({ areaIds: [] }) : undefined}
              showRemove={!!(filters?.areaIds && filters.areaIds.length > 0)}
            />
            <div ref={salaryButtonRef}>
              <FilterButton
                label={getSalaryLabel()}
                isActive={!!(filters?.salaryFrom || filters?.salaryTo)}
                onClick={handleOpenSalaryPopup}
                onRemove={onFiltersChange && (filters?.salaryFrom || filters?.salaryTo) ? () => onFiltersChange({ salaryFrom: '', salaryTo: '' }) : undefined}
                showRemove={!!(filters?.salaryFrom || filters?.salaryTo)}
              />
            </div>
            {(() => {
              const desired = filters?.desiredPosition?.trim() || '';
              const current = filters?.currentPosition?.trim() || '';
              const hasPositionFilter = !!(desired || current);
              const parts: string[] = [];
              if (desired) parts.push(`желаемая: ${desired}`);
              if (current) parts.push(`текущая: ${current}`);
              const positionLabel = hasPositionFilter
                ? `Должность · ${parts.join(', ')}`
                : 'Должность';
              return (
                <FilterButton
                  label={positionLabel}
                  isActive={hasPositionFilter}
                  onClick={() => openResponsesFilters()}
                  onRemove={
                    onFiltersChange && hasPositionFilter
                      ? () => onFiltersChange({ desiredPosition: '', currentPosition: '' })
                      : undefined
                  }
                  showRemove={hasPositionFilter}
                />
              );
            })()}
            <div className="relative" ref={collectionButtonRef as any}>
              <FilterButton
                label={getCollectionLabel()}
                isActive={!!(filters?.collection && filters.collection !== 'response')}
                onClick={() => setCollectionDropdownOpen(!collectionDropdownOpen)}
                onRemove={onFiltersChange && filters?.collection && filters.collection !== 'response' ? () => onFiltersChange({ collection: 'response' }) : undefined}
                showRemove={!!(filters?.collection && filters.collection !== 'response')}
              />
              {collectionDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-[9998]" onClick={() => setCollectionDropdownOpen(false)} />
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 p-3 z-[9999] min-w-[280px] max-h-[400px] overflow-y-auto">
                    <div className="space-y-1">
                      {EMPLOYER_STATES_OPTIONS.filter(o => o.value !== 'response').map(option => (
                        <label key={option.value} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg">
                          <input
                            type="radio"
                            name="collection"
                            checked={filters?.collection === option.value}
                            onChange={() => handleCollectionChange(option.value)}
                            className="w-4 h-4 text-[#E16349] border-gray-300 focus:ring-[#E16349]"
                          />
                          <span className="text-sm text-[#0A0D14]">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>}

        {isAvitoResponsesSource && filters && onFiltersChange && (
          <div className="flex flex-wrap items-center gap-[8px]">
            <div className="relative" ref={avitoStatusButtonRef}>
              <FilterButton
                label={getAvitoStatusFilterLabel()}
                isActive={isAvitoApplicationStateChanged()}
                onClick={() => setAvitoStatusDropdownOpen(!avitoStatusDropdownOpen)}
                onRemove={isAvitoApplicationStateChanged() ? () => onFiltersChange({ avitoApplicationState: DEFAULT_AVITO_APPLICATION_STATE }) : undefined}
                showRemove={isAvitoApplicationStateChanged()}
              />
              {avitoStatusDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-[9998]" onClick={() => setAvitoStatusDropdownOpen(false)} />
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 p-3 z-[9999] min-w-[280px] max-h-[360px] overflow-y-auto">
                    <div className="text-sm font-medium text-[#0A0D14] px-2 pb-2">Статус</div>
                    <div className="space-y-1">
                      {getAvitoStatusOptions().map((option) => (
                        <label key={option.value} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg">
                          <input
                            type="radio"
                            name="avitoApplicationState"
                            checked={(filters.avitoApplicationState || DEFAULT_AVITO_APPLICATION_STATE) === option.value}
                            onChange={() => handleAvitoStatusChange(option.value)}
                            className="w-4 h-4 text-[#E16349] border-gray-300 focus:ring-[#E16349]"
                          />
                          <span className="text-sm text-[#0A0D14]">{option.label}</span>
                        </label>
                      ))}
                      {avitoStatesLoading && (
                        <div className="px-2 py-1 text-sm text-[#868C98]">Загрузка статусов...</div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            <FilterButton
              label={getAgeLabel()}
              isActive={!!(filters.ageFrom || filters.ageTo)}
              onClick={() => openResponsesFilters('age')}
              onRemove={filters.ageFrom || filters.ageTo ? () => onFiltersChange({ ageFrom: '', ageTo: '' }) : undefined}
              showRemove={!!(filters.ageFrom || filters.ageTo)}
            />
            <FilterButton
              label={getAvitoExperienceLabel()}
              isActive={!!(filters.avitoExperienceMin || filters.avitoExperienceMax)}
              onClick={() => openResponsesFilters('experience')}
              onRemove={filters.avitoExperienceMin || filters.avitoExperienceMax ? () => onFiltersChange({ avitoExperienceMin: '', avitoExperienceMax: '' }) : undefined}
              showRemove={!!(filters.avitoExperienceMin || filters.avitoExperienceMax)}
            />
            <FilterButton
              label={filters.areaIds.length > 0 ? `Локация (${filters.areaIds.length})` : 'Локация'}
              isActive={filters.areaIds.length > 0}
              onClick={() => openResponsesFilters('area')}
              onRemove={filters.areaIds.length > 0 ? () => onFiltersChange({ areaIds: [] }) : undefined}
              showRemove={filters.areaIds.length > 0}
            />
            <FilterButton
              label={getSalaryLabel()}
              isActive={!!(filters.salaryFrom || filters.salaryTo)}
              onClick={() => openResponsesFilters('salary')}
              onRemove={filters.salaryFrom || filters.salaryTo ? () => onFiltersChange({ salaryFrom: '', salaryTo: '' }) : undefined}
              showRemove={!!(filters.salaryFrom || filters.salaryTo)}
            />
            <FilterButton
              label={getAvitoEducationLabel()}
              isActive={(filters.avitoEducationLevel?.length || 0) > 0}
              onClick={() => openResponsesFilters('education')}
              onRemove={(filters.avitoEducationLevel?.length || 0) > 0 ? () => onFiltersChange({ avitoEducationLevel: [] }) : undefined}
              showRemove={(filters.avitoEducationLevel?.length || 0) > 0}
            />
            <FilterButton
              label={getAvitoDriverLabel()}
              isActive={!!(filters.avitoDriverLicence || filters.avitoDriverLicenceCategory.length > 0)}
              onClick={() => openResponsesFilters('driver')}
              onRemove={filters.avitoDriverLicence || filters.avitoDriverLicenceCategory.length > 0 ? () => onFiltersChange({ avitoDriverLicence: '', avitoDriverLicenceCategory: [] }) : undefined}
              showRemove={!!(filters.avitoDriverLicence || filters.avitoDriverLicenceCategory.length > 0)}
            />
          </div>
        )}

      </div>

      {isHhResponsesSource && parseMode === 'manual' && (
        <div className="bg-white rounded-2xl p-6 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-[#0A0D14] whitespace-nowrap">Количество кандидатов</span>
          <input
            type="number"
            min={1}
            max={50}
            value={relevanceCount ?? 10}
            onChange={(e) => setCount(Math.min(50, Math.max(1, parseInt(e.target.value || '1', 10))))}
            className="h-8 w-14 px-3 rounded-[12px] border border-[#E2E4E9] bg-white text-sm text-[#0A0D14] text-center no-spinner"
          />
          <span className="text-sm font-medium text-[#0A0D14] whitespace-nowrap">с релевантностью:</span>
          <div className="flex items-center gap-[8px]">
            {([
              { id: 'high' as const, label: '≥85%' },
              { id: 'medium' as const, label: '70-84%' },
              { id: 'low' as const, label: '<70%' },
            ]).map((opt) => {
              const active = relevancePreset === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setPreset(active ? null : opt.id)}
                  className={`h-8 rounded-[16px] px-[12px] py-[6px] text-[14px] font-medium leading-5 transition-colors shrink-0 ${
                    active ? 'text-white' : 'bg-[#F3F4F6] text-[#0A0D14] hover:bg-[#EDEFF2]'
                  }`}
                  style={active ? { backgroundColor: colors.primary } : undefined}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {(relevancePreset || responsesRelevanceApplied) && (
            <button
              type="button"
              onClick={() => {
                setPreset(null);
                if (onRelevanceClear) onRelevanceClear();
              }}
              className="text-sm font-medium text-[#525866] hover:text-[#0A0D14] transition-colors whitespace-nowrap"
            >
              Сбросить
            </button>
          )}
          {responsesRelevanceLoading && (
            <div className="flex items-center gap-3 text-sm text-[#525866]">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderBottomColor: colors.primary }} />
              <span>
                {searchProgress && (searchProgress.status === 'processing' || searchProgress.status === 'started')
                  ? `Найдено ${searchProgress.collected} из ${searchProgress.target} · проанализировано ${searchProgress.processed}`
                  : 'Оценка резюме…'}
              </span>
              {/* Кнопка показывается всегда пока идёт loader — WS-прогресс не обязателен для отмены.
                  Цвет — брендирование (colors.primary), форма та же что и primary-кнопки проекта. */}
              {onCancelSearch && (
                <button
                  type="button"
                  onClick={onCancelSearch}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] text-white text-sm font-medium transition-opacity hover:opacity-90"
                  style={{ backgroundColor: colors.primary }}
                >
                  <XMarkIcon className="w-3.5 h-3.5" />
                  Остановить
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {isHhResponsesSource && parseMode === 'auto' && (
        <div className="bg-white rounded-2xl p-6 flex flex-wrap lg:flex-nowrap items-center gap-6">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-[#0A0D14]">Все с релевантностью:</span>
              {AUTO_PARSE_BUCKETS.map((bucket) => (
                <div key={bucket.id} className="inline-flex items-center gap-2 shrink-0">
                  <span className="px-2 py-0.5 bg-[#F3F4F6] rounded-xl text-xs font-medium text-[#0A0D14]">
                    {bucket.label}
                  </span>
                  <select
                    value={autoParseConfig[bucket.id] || bucket.defaultAction}
                    onChange={(event) => {
                      const action = event.target.value as AutoParseAction;
                      setAutoParseConfig((prev) => ({ ...prev, [bucket.id]: action }));
                    }}
                    disabled={autoParseRunning}
                    className="h-7 px-2 bg-white border border-[#E2E4E9] rounded-xl text-xs text-[#525866] shadow-sm disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#E16349]"
                    title={`${bucket.label}: ${autoParseBucketCounts[bucket.id] || 0} откликов`}
                  >
                    {(['invite', 'clarify', 'reject'] as AutoParseAction[]).map((action) => (
                      <option key={action} value={action}>
                        {AUTO_PARSE_ACTION_LABELS[action]}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={handleStartAutoParse}
            className="ml-auto shrink-0 flex items-center gap-1 px-4 py-2.5 bg-[#E16349] text-white rounded-xl text-sm font-medium hover:bg-[#c9573f] transition-colors"
          >
            {autoParseRunning ? 'Остановить' : 'Запустить'}
            {autoParseRunning ? (
              <XMarkIcon className="w-5 h-5" />
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M6.3 2.8A1.5 1.5 0 004 4.1v11.8a1.5 1.5 0 002.3 1.3l9-5.9a1.5 1.5 0 000-2.6l-9-5.9z" />
              </svg>
            )}
          </button>
        </div>
      )}

      {selectedCount > 0 && (
        <div className="bg-white rounded-2xl p-6 flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-gray-700">Действия:</span>
          <button
            type="button"
            onClick={() => onSelectedResponsesChange(new Set())}
            className="inline-flex items-center px-3 py-2.5 border border-[#E2E4E9] text-sm font-medium rounded-xl text-[#0A0D14] bg-[#F6F8FA] hover:bg-gray-100"
          >
            Отменить выбор {selectedCount}
          </button>
          {isAvitoResponsesSource ? (
            <>
              <select
                value={selectedAvitoAction}
                onChange={(event) => setSelectedAvitoAction(event.target.value)}
                disabled={avitoStatesLoading || avitoBulkActionInProgress}
                className="h-10 px-3 rounded-xl border border-[#E2E4E9] bg-white text-sm text-[#0A0D14] disabled:opacity-50"
              >
                <option value="">{avitoStatesLoading ? 'Загрузка статусов...' : 'Выберите статус'}</option>
                {avitoApplicationStates.map((state: any) => (
                  <option key={state.id} value={state.id}>
                    {state.name || state.description || state.id}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={!selectedAvitoAction || avitoBulkActionInProgress}
                onClick={handleAvitoApplySelectedStatus}
                className={`inline-flex items-center px-3 py-2.5 border border-[#E2E4E9] text-sm font-medium rounded-xl ${
                  !selectedAvitoAction || avitoBulkActionInProgress
                    ? 'text-[#868C98] bg-[#F6F8FA] cursor-not-allowed opacity-60'
                    : 'text-[#0A0D14] bg-[#F6F8FA] hover:bg-gray-100'
                }`}
              >
                {avitoBulkActionInProgress ? 'Сохраняем...' : 'Сменить статус'}
              </button>
              <button
                type="button"
                disabled={avitoMailingInProgress}
                onClick={() => handleAvitoCreateCampaign('clarify')}
                className={`inline-flex items-center px-3 py-2.5 border border-[#E2E4E9] text-sm font-medium rounded-xl ${
                  avitoMailingInProgress
                    ? 'text-[#868C98] bg-[#F6F8FA] cursor-not-allowed opacity-60'
                    : 'text-[#0A0D14] bg-[#F6F8FA] hover:bg-gray-100'
                }`}
              >
                {avitoMailingInProgress ? 'Создаем...' : 'Уточнить опыт'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => {
                  const selected = getSelectedResponsesList();
                  const allInPhoneInterview = selected.length > 0 && selected.every((r: any) => (r.employer_state?.id || r.state?.id) === 'phone_interview');
                  const inviteActionId = allInPhoneInterview ? 'interview' : 'phone_interview';
                  openBulkActionModal({ actionId: inviteActionId, templateId: 'invite_after_response' });
                }}
                className="inline-flex items-center px-3 py-2.5 border border-[#E2E4E9] text-sm font-medium rounded-xl text-[#0A0D14] bg-[#F6F8FA] hover:bg-gray-100"
              >
                Пригласить
              </button>
              <button
                type="button"
                onClick={() => {
                  openBulkActionModal({ actionId: 'discard_by_employer', templateId: 'discard_after_response' });
                }}
                className="inline-flex items-center px-3 py-2.5 border border-[#E2E4E9] text-sm font-medium rounded-xl text-[#0A0D14] bg-[#F6F8FA] hover:bg-gray-100"
              >
                Отказать
              </button>
              <button
                type="button"
                disabled={bulkClarifyingInProgress}
                onClick={() => handleBulkClarifyExperience()}
                className={`inline-flex items-center px-3 py-2.5 border border-[#E2E4E9] text-sm font-medium rounded-xl ${
                  bulkClarifyingInProgress
                    ? 'text-[#868C98] bg-[#F6F8FA] cursor-not-allowed opacity-60'
                    : 'text-[#0A0D14] bg-[#F6F8FA] hover:bg-gray-100'
                }`}
              >
                {bulkClarifyingInProgress ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Обработка...
                  </>
                ) : 'Уточнить опыт'}
              </button>
            </>
          )}
        </div>
      )}

      <div>
        {responsesLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E16349]"></div>
            <span className="ml-3 text-gray-600">Загрузка откликов...</span>
          </div>
        ) : responsesData.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg font-medium">Нет откликов</p>
            <p className="text-sm mt-2">По привязанным вакансиям пока нет откликов</p>
          </div>
        ) : (
          <>
            <VacancyResponsesInline
              vacancyId={(selectedVacancy as any)?.hhVacancyId || (selectedVacancy as any)?.avitoVacancyId || ''}
              selectedVacancy={selectedVacancy}
              responses={responsesData}
              aiEvaluations={aiEvaluations}
              evaluatingIds={evaluatingIds}
              queuedEvaluations={queuedEvaluations}
              onEvaluateResume={onEvaluateResume}
              onRefresh={onRefresh}
              onRemoveResponses={onRemoveResponses}
              selectedResponses={selectedResponses}
              onSelectedResponsesChange={onSelectedResponsesChange}
              scoringCreditLimitHit={scoringCreditLimitHit}
            />
          </>
        )}
      </div>

      {/* Блок статистики AI-скоринга — аналог из ResultsTable.js (обычный поиск).
          Показывает за текущую сессию: всего оценено, сколько из кэша (бесплатно),
          сколько новых (списано N кредитов). Скрывается если на вакансии
          выключен скоринг или ничего ещё не оценивалось. */}
      {vacancyScoringEnabled && scoringStats && scoringStats.total > 0 && !responsesLoading && (
        <div className="bg-white rounded-2xl px-6 py-3 flex flex-wrap items-center gap-4 text-xs">
          <span className="text-gray-500">
            Проанализировано: <span className="font-medium text-gray-700">{scoringStats.total}</span>
          </span>
          {/* Показываем оба показателя всегда (даже при 0) — чтобы пользователь
              сразу видел распределение "бесплатно/новых", а не гадал почему
              какой-то из них пропал. В обычном поиске блок прячет при 0,
              но на откликах пользователь явно попросил видеть и нули. */}
          <span className="text-green-600">
            Из кэша (бесплатно): <span className="font-medium">{scoringStats.fromCache}</span>
          </span>
          <span className="text-gray-500">
            Новых оценок: <span className="font-medium text-gray-700">{scoringStats.newlyScored} кр.</span>
          </span>
        </div>
      )}

      {pagination && totalPages > 1 && !responsesLoading && (
        <div className="px-6 py-4 border-t border-gray-200 bg-white rounded-2xl">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-gray-700">
              Показано {pageIndex * perPage + 1} - {Math.min((pageIndex + 1) * perPage, totalItems)} из {totalItems}
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => pagination.onPageChange(Math.max(currentPage - 1, 1))}
                disabled={currentPage <= 1}
                className="p-2 rounded-3xl border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <span className="sr-only">Назад</span>
                <ChevronLeftIcon className="h-5 w-5" />
              </button>

              <div className="flex items-center gap-1">
                {pageNumbers.map((page, index) => {
                  if (typeof page !== 'number') {
                    return (
                      <span
                        key={`ellipsis-${index}`}
                        className="flex h-8 min-w-8 items-center justify-center px-2 text-sm text-gray-500"
                      >
                        ...
                      </span>
                    );
                  }

                  const isActive = page === currentPage;

                  return (
                    <button
                      key={page}
                      type="button"
                      aria-current={isActive ? 'page' : undefined}
                      onClick={() => pagination.onPageChange(page)}
                      className={`flex h-8 min-w-8 items-center justify-center rounded-xl px-3 text-sm font-medium transition-colors ${isActive ? 'text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                      style={isActive ? { backgroundColor: colors.primary } : undefined}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={() => pagination.onPageChange(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage >= totalPages}
                className="p-2 rounded-3xl border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <span className="sr-only">Вперёд</span>
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <ResponseAutoParseStartModal
        open={autoParseModalOpen}
        loadingTemplates={autoParseTemplatesLoading}
        config={autoParseConfig}
        counts={autoParseBucketCounts}
        parseLimit={responsesLoadLimit}
        availableCount={getAutoParseAvailableCount()}
        inviteMessage={autoParseInviteMessage}
        rejectMessage={autoParseRejectMessage}
        onClose={() => setAutoParseModalOpen(false)}
        onApply={() => {
          setAutoParseModalOpen(false);
          void runAutoParse();
        }}
        onParseLimitChange={(value) => onResponsesLoadLimitChange?.(value)}
        onConfigChange={(bucketId, action) => setAutoParseConfig((prev) => ({ ...prev, [bucketId]: action }))}
        onInviteMessageChange={setAutoParseInviteMessage}
        onRejectMessageChange={setAutoParseRejectMessage}
      />

      <ActionModal
        isOpen={actionModalResponses.length > 0}
        onClose={handleCloseActionModal}
        responses={actionModalResponses}
        vacancyId={(selectedVacancy as any)?.hhVacancyId || (selectedVacancy as any)?.avitoVacancyId || ''}
        positionId={(selectedVacancy as any)?.id ?? null}
        positionSlug={(selectedVacancy as any)?.publicSlug ?? null}
        aiScreeningEnabled={Boolean((selectedVacancy as any)?.aiScreeningEnabled)}
        initialActionId={actionModalInitial?.actionId ?? null}
        initialTemplateId={actionModalInitial?.templateId ?? null}
        onSuccess={handleActionModalSuccess}
        onRemoveResponses={onRemoveResponses}
      />

      <ExperienceModal
        isOpen={isExperienceModalOpen}
        onClose={() => setIsExperienceModalOpen(false)}
        selectedValues={{
          totalExperience: filters?.experience || [],
          positionExperience: [],
          experiencePeriod: ''
        }}
        onSelectionChange={handleExperienceChange}
        onlyTotalExperience
      />

      <AreaModal
        isOpen={isAreaModalOpen}
        onClose={() => setIsAreaModalOpen(false)}
        selectedAreas={filters?.areaIds || []}
        onApply={handleAreaChange}
      />

      {isAgePopupOpen && filters && onFiltersChange && (
        <FilterPopup
          filterType="responses-age"
          isOpen={isAgePopupOpen}
          onClose={() => setIsAgePopupOpen(false)}
          filters={defaultFiltersState}
          onFiltersChange={() => {}}
          position={agePopupPosition}
          responsesFilters={filters}
          onResponsesFiltersChange={onFiltersChange}
        />
      )}

      {isSalaryPopupOpen && filters && onFiltersChange && (
        <FilterPopup
          filterType="responses-salary"
          isOpen={isSalaryPopupOpen}
          onClose={() => setIsSalaryPopupOpen(false)}
          filters={defaultFiltersState}
          onFiltersChange={() => {}}
          position={salaryPopupPosition}
          responsesFilters={filters}
          onResponsesFiltersChange={onFiltersChange}
        />
      )}

      {filters && onFiltersChange && (
        <ResponsesFiltersModal
          isOpen={isFiltersModalOpen}
          onClose={closeResponsesFilters}
          filters={filters}
          onFiltersChange={onFiltersChange}
          onApply={() => {
            if (onSearch) onSearch();
          }}
          onReset={() => {
            if (onClearFilters) onClearFilters();
          }}
          source={responsesSource}
          initialSection={filtersInitialSection}
        />
      )}
      {estaffLinkGuardModal}
      <CampaignCheckResultsModal
        open={checkResultsModalOpen}
        onClose={handleCheckCancel}
        onConfirm={handleCheckConfirm}
        loading={bulkClarifyingInProgress}
        checkResult={checkResult}
        message={checkResultMessage}
        messageType="warning"
      />
    </div>
  );
};

export default ResponsesTab;
